import { DataType } from "../../../services/CompanyService";
import {
  getInvoiceAdditionalValue,
  getInvoiceFieldDisplayValue
} from "../../../services/InvoiceService";
import { TranslationService } from "../../../services/TranslationService";
import { formatIntizaDate } from "../../../utils/FormatUtils";
import Dropdown from "../../shared/components/Dropdown";
import {
  AutocompleteGroup,
  DatePickerEditor,
  TextEditor,
  TextEditorNumber
} from "../../shared/components/Editors";
import { EntityField } from "../../shared/entities/EntityField";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import { InvoiceEditStatus } from "./entities/InvoiceEditStatus";
import { InvoiceGetResponse } from "./entities/InvoiceGetResponse";

export class InvoiceMassiveChangesFieldEditProps {
  public onlyEditor?: boolean = false;
  public invoice?: InvoiceGetResponse.Item;
  public status?: InvoiceEditStatus = undefined;
  public halfSize?: boolean = false;
  public requiredManager?: RequiredManager | undefined;
  public fieldsToValidate?: number[] | undefined;
  public readOnly?: boolean = false;
  public defaultValue?: string | undefined;
  constructor(
    public field: EntityField,
    public onChange: (value?: string) => void,
    public personId: string
  ) {}
}

export const ifOldFormat = (dateString: string) => {
  return dateString.includes("-")
    ? formatIntizaDate(new Date(dateString))
    : dateString;
};

export const InvoiceMassiveChangesFieldEdit = ({
  field,
  onChange,
  onlyEditor,
  invoice,
  status,
  halfSize,
  personId,
  requiredManager,
  fieldsToValidate,
  readOnly,
  defaultValue
}: InvoiceMassiveChangesFieldEditProps) => {
  const { translate } = TranslationService;
  const title = field.title;
  let displayValue = invoice
    ? getInvoiceFieldDisplayValue(field, invoice)
    : undefined;
  // Si hay un defaultValue y no hay valor actual, usar el defaultValue
  if (
    defaultValue &&
    !displayValue &&
    !status?.additionals.find((a) => a.Id === field.id.toString())?.Value
  ) {
    displayValue = defaultValue;
  }
  let sizeCol = halfSize ? " col-md-6" : "";
  let editor = undefined;
  // Verificar si el campo es obligatorio
  const isRequired =
    requiredManager &&
    fieldsToValidate &&
    fieldsToValidate.includes(field.id) &&
    !readOnly;

  // Debug: console.log para ver qué está pasando
  if (process.env.NODE_ENV === "development") {
    const fieldIsInList = fieldsToValidate?.includes(field.id) ?? false;
    if (field.id === 47952) {
      console.log(`Field 47952 check:`, {
        fieldId: field.id,
        fieldTitle: title,
        fieldsToValidate,
        fieldIsInList,
        readOnly,
        isRequired
      });
    }
  }

  if (isRequired) {
    onChange = requiredManager.makeRequired(onChange);
  }

  // Obtener el valor actual del campo para ValidationMessage
  const currentFieldValue = status?.additionals?.find(
    (x) => x.Id === field.id.toString() || x.Id === field.id
  )?.Value;
  // Para campos de fecha, usar el valor formateado si existe, o el defaultValue
  const validationDefaultValue =
    field.type === DataType.Date
      ? currentFieldValue
        ? currentFieldValue.includes("-")
          ? ifOldFormat(currentFieldValue)
          : currentFieldValue
        : defaultValue
          ? ifOldFormat(defaultValue)
          : undefined
      : currentFieldValue || defaultValue;

  switch (field.type) {
    case DataType.List:
      const drpItems = field.listItems.map((x) => ({
        value: x.id.toString(),
        text: x.value
      }));
      const selectedValue = drpItems.find(
        (x) => x.text === displayValue
      )?.value;
      editor = (
        <Dropdown
          optionLabel={translate.Select}
          onChange={onChange}
          items={drpItems}
          defaultValue={selectedValue}
          readonly={readOnly}
        />
      );
      break;
    case DataType.Date:
      let date = invoice
        ? getInvoiceAdditionalValue(field, invoice)
        : undefined;
      let val = date
        ? ifOldFormat(date)
        : defaultValue
          ? ifOldFormat(defaultValue)
          : undefined;
      editor = (
        <DatePickerEditor
          onChange={onChange}
          defaultValue={val}
          disabled={readOnly}
        />
      );
      break;
    case DataType.Number:
    case DataType.Currency:
      editor = (
        <TextEditorNumber
          onChange={onChange}
          defaultValue={displayValue || defaultValue}
          disabled={readOnly}
        />
      );
      break;
    case DataType.Group:
      editor = (
        <AutocompleteGroup
          onChange={(x) => onChange(x?.value)}
          clientId={personId}
        />
      );
      break;
    case DataType.Phone:
    case DataType.Text:
    case DataType.Link:
    default:
      editor = (
        <div>
          <TextEditor
            onChange={onChange}
            defaultValue={displayValue || defaultValue}
            disabled={readOnly}
          />
        </div>
      );
      break;
  }

  return onlyEditor ? (
    <>
      {editor}
      <ValidationMessage
        onChange={onChange}
        defaultValue={validationDefaultValue}
      />
    </>
  ) : (
    <div className={"d-flex input-column mb-3" + sizeCol}>
      <label className="form-label">{title}</label>
      {editor}
      <ValidationMessage
        onChange={onChange}
        defaultValue={validationDefaultValue}
      />
    </div>
  );
};

export default InvoiceMassiveChangesFieldEdit;
